import { useState, useEffect, useRef, useCallback } from "react";

export const useSpeechRecognition = (onResult, onError) => {
  const [isRecording, setIsRecording] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [transcript, setTranscript] = useState("");
  const [confidence, setConfidence] = useState(0);
  const recognitionRef = useRef(null);
  const timeoutRef = useRef(null);
  const silenceTimeoutRef = useRef(null);

  const startRecording = useCallback(() => {
    if (recognitionRef.current && !isRecording) {
      try {
        setIsRecording(true);
        setIsListening(true);
        setTranscript("");
        setConfidence(0);

        // Auto-stop after 30 seconds
        timeoutRef.current = setTimeout(() => {
          stopRecording();
        }, 30000);

        recognitionRef.current.start();
      } catch (error) {
        console.error("Error starting recognition:", error);
        setIsRecording(false);
        setIsListening(false);
        onError?.("Error al iniciar el reconocimiento de voz");
      }
    }
  }, [isRecording, onError]);

  const stopRecording = useCallback(() => {
    if (recognitionRef.current && isRecording) {
      try {
        recognitionRef.current.stop();
        setIsRecording(false);
        setIsListening(false);

        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
          timeoutRef.current = null;
        }

        if (silenceTimeoutRef.current) {
          clearTimeout(silenceTimeoutRef.current);
          silenceTimeoutRef.current = null;
        }
      } catch (error) {
        console.error("Error stopping recognition:", error);
      }
    }
  }, [isRecording]);

  useEffect(() => {
    if ("webkitSpeechRecognition" in window || "SpeechRecognition" in window) {
      const SpeechRecognition =
        window.SpeechRecognition || window.webkitSpeechRecognition;
      const recognitionInstance = new SpeechRecognition();

      // Configuración optimizada
      recognitionInstance.continuous = true;
      recognitionInstance.interimResults = true;
      recognitionInstance.lang = "es-ES";
      recognitionInstance.maxAlternatives = 3;

      // Configuraciones adicionales para mejor precisión
      if (recognitionInstance.serviceURI) {
        recognitionInstance.serviceURI =
          "wss://www.google.com/speech-api/v2/recognize";
      }

      recognitionInstance.onstart = () => {
        setIsListening(true);
      };

      recognitionInstance.onresult = (event) => {
        let finalTranscript = "";
        let interimTranscript = "";
        let bestConfidence = 0;

        for (let i = event.resultIndex; i < event.results.length; i++) {
          const result = event.results[i];
          const transcript = result[0].transcript;
          const confidence = result[0].confidence || 0;

          if (result.isFinal) {
            finalTranscript += transcript;
            bestConfidence = Math.max(bestConfidence, confidence);
          } else {
            interimTranscript += transcript;
          }
        }

        setTranscript(finalTranscript || interimTranscript);
        setConfidence(bestConfidence);

        // Si hay resultado final y confianza aceptable
        if (finalTranscript.trim() && bestConfidence > 0.7) {
          setIsRecording(false);
          setIsListening(false);

          if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
          }

          if (onResult) {
            onResult(finalTranscript.trim(), bestConfidence);
          }

          recognitionInstance.stop();
        } else if (finalTranscript.trim()) {
          // Resultado con baja confianza, esperar un poco más
          if (silenceTimeoutRef.current) {
            clearTimeout(silenceTimeoutRef.current);
          }

          silenceTimeoutRef.current = setTimeout(() => {
            if (finalTranscript.trim()) {
              setIsRecording(false);
              setIsListening(false);
              onResult?.(finalTranscript.trim(), bestConfidence);
              recognitionInstance.stop();
            }
          }, 2000);
        }
      };

      recognitionInstance.onerror = (event) => {
        console.error("Speech recognition error:", event.error);
        setIsRecording(false);
        setIsListening(false);

        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
          timeoutRef.current = null;
        }

        let errorMessage = "Error en el reconocimiento de voz";
        switch (event.error) {
          case "no-speech":
            errorMessage =
              "No se detectó voz. Intenta hablar más cerca del micrófono";
            break;
          case "audio-capture":
            errorMessage = "No se pudo acceder al micrófono";
            break;
          case "not-allowed":
            errorMessage = "Permiso de micrófono denegado";
            break;
          case "network":
            errorMessage = "Error de conexión. Verifica tu internet";
            break;
          case "service-not-allowed":
            errorMessage = "Servicio de reconocimiento no disponible";
            break;
        }

        onError?.(errorMessage);
      };

      recognitionInstance.onend = () => {
        setIsRecording(false);
        setIsListening(false);

        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
          timeoutRef.current = null;
        }

        if (silenceTimeoutRef.current) {
          clearTimeout(silenceTimeoutRef.current);
          silenceTimeoutRef.current = null;
        }
      };

      recognitionRef.current = recognitionInstance;
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      if (silenceTimeoutRef.current) {
        clearTimeout(silenceTimeoutRef.current);
      }
    };
  }, [onResult, onError]);

  return {
    isRecording,
    isListening,
    transcript,
    confidence,
    startRecording,
    stopRecording,
    isSupported:
      "webkitSpeechRecognition" in window || "SpeechRecognition" in window,
  };
};
